/**
 * Minimum selectable date / month bounds for PaceHRM pickers.
 *
 * Rules:
 * - Company-wide month pickers (Attendance, Payroll, Process, Run Payroll, Payslips):
 *   min = Company.serviceCommencesOn (service commencement).
 * - Single-employee date pickers (leave, regularise, calendar, requests):
 *   min = max(service commencement, employee date of joining).
 * - Multi-employee company screens: do NOT use one employee's DOJ as month min;
 *   keep SC on the month strip and blank days before each employee's DOJ in the grid.
 */

import {
  getCompanyServiceCommencementDate,
  getEmployeeDoj,
} from "../lib/leaveBalanceDates";

const parseDateOnly = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = m[3] != null ? Number(m[3]) : 1;
    if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(y, mo - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(raw.length <= 10 ? `${raw.slice(0, 10)}T12:00:00` : raw);
  if (Number.isNaN(dt.getTime())) return null;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

export const toYmd = (value) => {
  const d = parseDateOnly(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const toYm = (value) => {
  const d = parseDateOnly(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

export const maxDate = (...values) => {
  const dates = values.map(parseDateOnly).filter(Boolean);
  if (!dates.length) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
};

export const minDate = (...values) => {
  const dates = values.map(parseDateOnly).filter(Boolean);
  if (!dates.length) return null;
  return new Date(Math.min(...dates.map((d) => d.getTime())));
};

/** Company service commencement as Date (local), or null. */
export const resolveCompanyMinDate = (serviceCommencement) => {
  const raw =
    serviceCommencement ??
    getCompanyServiceCommencementDate();
  return parseDateOnly(raw);
};

/**
 * Single-employee floor: max(service commencement, DOJ).
 * Pass employee object or a DOJ string/date.
 */
export const resolveEmployeeMinDate = (employeeOrDoj, serviceCommencement) => {
  const doj =
    employeeOrDoj && typeof employeeOrDoj === "object"
      ? getEmployeeDoj(employeeOrDoj)
      : employeeOrDoj;
  return maxDate(resolveCompanyMinDate(serviceCommencement), doj);
};

/** YYYY-MM for <input type="month"> / monthYearOnly pickers (company-level). */
export const resolveCompanyMinMonthValue = (serviceCommencement) =>
  toYm(resolveCompanyMinDate(serviceCommencement));

/** YYYY-MM for single-employee month pickers. */
export const resolveEmployeeMinMonthValue = (employeeOrDoj, serviceCommencement) =>
  toYm(resolveEmployeeMinDate(employeeOrDoj, serviceCommencement));

/** yyyy-mm-dd for DatePicker / input type="date" (company-level). */
export const resolveCompanyMinDateValue = (serviceCommencement) =>
  toYmd(resolveCompanyMinDate(serviceCommencement));

/** yyyy-mm-dd for single-employee date pickers. */
export const resolveEmployeeMinDateValue = (employeeOrDoj, serviceCommencement) =>
  toYmd(resolveEmployeeMinDate(employeeOrDoj, serviceCommencement));

/**
 * Clamp a YYYY-MM value to [minYm, maxYm] (inclusive). Empty parts skipped.
 */
export const clampYm = (ym, minYm, maxYm) => {
  let next = String(ym || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(next)) return next;
  if (minYm && /^\d{4}-\d{2}$/.test(minYm) && next < minYm) next = minYm;
  if (maxYm && /^\d{4}-\d{2}$/.test(maxYm) && next > maxYm) next = maxYm;
  return next;
};

/**
 * Year list for month/year dropdowns (company SC floor).
 * @returns {number[]}
 */
export const buildYearOptionsFromMin = ({
  minDate,
  currentYear = new Date().getFullYear(),
  yearsBefore = 3,
  yearsAfter = 1,
} = {}) => {
  const floor = parseDateOnly(minDate);
  const start = floor
    ? Math.min(floor.getFullYear(), currentYear)
    : currentYear - yearsBefore;
  const end = currentYear + yearsAfter;
  const from = Math.min(start, end);
  const to = Math.max(start, end);
  const out = [];
  for (let y = from; y <= to; y += 1) out.push(y);
  return out;
};

/**
 * Whether a 0-based month is before company/employee min in that year.
 */
export const isMonthIndexBeforeMin = (year, monthIndex0, minDate) => {
  const floor = parseDateOnly(minDate);
  if (!floor || year == null || monthIndex0 == null) return false;
  if (Number(year) < floor.getFullYear()) return true;
  if (Number(year) > floor.getFullYear()) return false;
  return Number(monthIndex0) < floor.getMonth();
};

/**
 * Clamp year + 0-based month to not be before minDate.
 * @returns {{ year: number, month: number }}
 */
export const clampYearMonthIndex = (year, monthIndex0, minDate) => {
  let y = Number(year);
  let m = Number(monthIndex0);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return { year, month: monthIndex0 };
  const floor = parseDateOnly(minDate);
  if (!floor) return { year: y, month: m };
  if (
    y < floor.getFullYear() ||
    (y === floor.getFullYear() && m < floor.getMonth())
  ) {
    return { year: floor.getFullYear(), month: floor.getMonth() };
  }
  return { year: y, month: m };
};

/**
 * Clamp a yyyy-mm-dd value to [minYmd, maxYmd].
 */
export const clampYmd = (ymd, minYmd, maxYmd) => {
  let next = String(ymd || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(next)) return next;
  if (minYmd && /^\d{4}-\d{2}-\d{2}$/.test(minYmd) && next < minYmd) next = minYmd;
  if (maxYmd && /^\d{4}-\d{2}-\d{2}$/.test(maxYmd) && next > maxYmd) next = maxYmd;
  return next;
};

export { parseDateOnly, getCompanyServiceCommencementDate, getEmployeeDoj };
